import { useState, useEffect, useRef, useMemo } from 'react'
import { get, ref } from "firebase/database";
import toast from 'react-hot-toast';

import ModalCustom from '../../components/utils/ModalCustom.js';

import ShowUserDetails from './ShowUserDetails.js';
import DateRangePickerWidget from './DateRangePicker.js';
import BillingChart from './BillingChart.js';

import { formatDate, formatDateLong } from '../../utils/formatDates.js';
import { db } from '../../utils/firebaseApp.js';

export default function ExtractionsDashboard({ users }) {
    const [billing, setBilling] = useState([]);
    const [billingChart, setBillingChart] = useState([]);
    const [pagesChart, setPagesChart] = useState([]); // New state for pages chart
    const [totalRequests, setTotalRequests] = useState(0);
    const [totalPages, setTotalPages] = useState(0); // New state for total pages

    // Pagination state for otherBilling
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Debounce search term with 300ms delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const today = new Date();
    const [startTime, setStartTime] = useState(new Date(today.getFullYear(), today.getMonth(), 1, 0, 0).getTime());
    const [endTime, setEndTime] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59).getTime());
    const [tempStartTime, setTempStartTime] = useState(new Date(today.getFullYear(), today.getMonth(), 1, 0, 0).getTime());
    const [tempEndTime, setTempEndTime] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59).getTime());

    const [selectedUser, setSelectedUser] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');

    // admin emails that need to be included in extracta.ai
    const adminEmails = ['barzoiu.alexandru@gmail.com', 'office@extracta.ai', "alexraducu03@gmail.com"];

    // Helper function to check if the email belongs to extracta.ai
    const isExtractaUser = (userId) => {
        const email = users[userId];
        return email && email.endsWith('@extracta.ai') && (email.includes('eden') || email.includes('rapid'));
    };

    // Helper function to check if the email belongs to the team
    const isTeamUser = (userId) => {
        const email = users[userId];
        return email && adminEmails.includes(email);
    };

    // Memoize filtering billing data into arrays based on the domain
    const { extractaAiBilling, teamBilling, otherBilling } = useMemo(() => {
        const extractaAiBilling = billing.filter(userBilling => isExtractaUser(userBilling.userId));
        const teamBilling = billing.filter(userBilling => isTeamUser(userBilling.userId));
        const otherBilling = billing.filter(userBilling => !isExtractaUser(userBilling.userId) && !isTeamUser(userBilling.userId));

        return { extractaAiBilling, teamBilling, otherBilling };
    }, [billing, users]);

    // Memoize filtered otherBilling by search term
    const filteredOtherBilling = useMemo(() => {
        return otherBilling.filter(userBilling => {
            const email = users[userBilling.userId] || userBilling.userId;
            return email.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
        });
    }, [otherBilling, debouncedSearchTerm, users]);

    // Memoize pagination logic for filtered otherBilling
    const paginationData = useMemo(() => {
        const totalOtherBillingPages = Math.ceil(filteredOtherBilling.length / itemsPerPage);
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        const currentOtherBilling = filteredOtherBilling.slice(indexOfFirstItem, indexOfLastItem);

        return { totalOtherBillingPages, currentOtherBilling };
    }, [filteredOtherBilling, currentPage, itemsPerPage]);

    // Reset to first page when data changes or search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [billing, itemsPerPage, debouncedSearchTerm]);

    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
    const [open, setOpen] = useState(false);

    const handleButtonClick = (userId) => {
        console.log('Button clicked for user:', userId);
        setSelectedUserId(userId);
        setOpen(true);
    }

    const updateDates = (dates) => {
        // Check if 'from' date is valid
        if (!dates.from || isNaN(new Date(dates.from).getTime())) {
            return; // Stop the function if 'from' date is not valid
        }

        // Update startTime to 'from' date at 00:00
        const fromStartOfDay = new Date(dates.from);
        fromStartOfDay.setHours(0, 0, 0, 0);
        setTempStartTime(fromStartOfDay.getTime());

        // Update endTime based on the presence of 'to' date
        let toEndOfDay;
        if (dates.to && !isNaN(new Date(dates.to).getTime())) {
            // 'to' is valid, use 'to' date at 23:59
            toEndOfDay = new Date(dates.to);
        } else {
            // 'to' is not valid, use 'from' date at 23:59
            toEndOfDay = new Date(dates.from);
        }
        toEndOfDay.setHours(23, 59, 59, 999);
        setTempEndTime(toEndOfDay.getTime());
    }

    const updateDatesAndFetch = () => {
        // Check if both tempStartTime and tempEndTime are present and valid
        if (tempStartTime && tempEndTime && !isNaN(new Date(tempStartTime).getTime()) && !isNaN(new Date(tempEndTime).getTime())) {
            // Update startTime and endTime with tempStartTime and tempEndTime
            setStartTime(tempStartTime);
            setEndTime(tempEndTime);

            // show toast
            toast.success('Dates updated successfully');
        }
    };

    const changeSelectedUser = (userId) => {
        setSelectedUser(userId);
    }

    const fetchBilling = async () => {
        const toastId = toast.loading('Fetching billing data...');

        // Fetch all user billing data within the time range
        const billingRef = ref(db, 'billing');
        get(billingRef).then((snapshot) => {
            if (snapshot.exists()) {
                let allUsersData = snapshot.val();

                let testUserIds = [
                    'WgVLxXREk7eW2aQeFOSfRbuxJbm1', // barzoiu
                    '3fEgxedJEsfxRT1V4x4NLxgppbE2', // extracta
                    'AYPeMWkuo3e1cE8WFZHG8Nr13IP2', // cristi
                    'HhEhbxIKIZV9S9pXnM7hWeCICQl1', // bardahan
                    'jDJ4hJMOt0fuCFZzOmj4tOTOL613', // cont test
                    'SemdChEmcgNSmCxfbiXQCEk3ndE3', // test extracta
                    'UU2xotn4DzN0ArEo0eOJk1ZkMms2', // rapidapi.testing@extracta.ai	
                    'yIfPRqjK8VZq351Q4WvauTamYNA3', // extracta test 2
                ];

                // filter all users data. i dont want the users that are in the testUserIds
                allUsersData = Object.keys(allUsersData).reduce((acc, userId) => {
                    if (!testUserIds.includes(userId)) {
                        acc[userId] = allUsersData[userId];
                    }
                    return acc;
                }, {});

                // if selectedUser is not empty, then filter the data to keep only that id
                if (selectedUser !== '') {
                    allUsersData = {
                        [selectedUser]: allUsersData[selectedUser]
                    };
                }

                const filteredData = [];
                const totalBillingData = [];
                const totalPagesData = []; // New array for pages data

                // Transform each user's data into an array element
                Object.keys(allUsersData).forEach((userId) => {
                    const userBilling = allUsersData[userId];
                    let userBillingData = { userId, billingItems: [] };

                    Object.keys(userBilling).forEach((billingId) => {
                        // Check if billingId is 'success' or 'error'
                        if (billingId === 'success' || billingId === 'error') {
                            // billingItem contains multiple billings
                            // Loop through each billing in the billingItem
                            Object.keys(userBilling[billingId]).forEach((billingItemId) => {
                                const billingItem = userBilling[billingId][billingItemId];

                                if (billingItem.timestamp >= startTime && billingItem.timestamp <= endTime) {
                                    userBillingData.billingItems.push(billingItem);

                                    const timestamp = billingItem.timestamp;
                                    const day = formatDate(new Date(timestamp));
                                    const pages = billingItem.pagesNo || 1; // Get pages or default to 0

                                    // Check if a billing entry for this timestamp already exists
                                    const existingEntry = totalBillingData.find((entry) => entry.day === day);
                                    const existingPagesEntry = totalPagesData.find((entry) => entry.day === day);

                                    if (existingEntry) {
                                        existingEntry.Requests += 1;
                                    } else {
                                        // Create a new entry for this timestamp
                                        const newEntry = {
                                            date: timestamp,
                                            day: day,
                                            Requests: 1,
                                        };

                                        totalBillingData.push(newEntry);
                                    }

                                    if (existingPagesEntry) {
                                        existingPagesEntry.Pages += pages;
                                    } else {
                                        // Create a new entry for this timestamp
                                        const newEntry = {
                                            date: timestamp,
                                            day: day,
                                            Pages: pages,
                                        };

                                        totalPagesData.push(newEntry);
                                    }
                                }
                            });

                            return; // Stop the function
                        }

                        const billingItem = userBilling[billingId];
                        if (billingItem.timestamp >= startTime && billingItem.timestamp <= endTime) {
                            userBillingData.billingItems.push(billingItem);

                            const timestamp = billingItem.timestamp;
                            const day = formatDate(new Date(timestamp));
                            const pages = billingItem.pagesNo || 1; // Get pages or default to 0

                            // Check if a billing entry for this timestamp already exists
                            const existingEntry = totalBillingData.find((entry) => entry.day === day);
                            const existingPagesEntry = totalPagesData.find((entry) => entry.day === day);

                            if (existingEntry) {
                                existingEntry.Requests += 1;
                            } else {
                                // Create a new entry for this timestamp
                                const newEntry = {
                                    date: timestamp,
                                    day: day,
                                    Requests: 1,
                                };

                                totalBillingData.push(newEntry);
                            }

                            if (existingPagesEntry) {
                                existingPagesEntry.Pages += pages;
                            } else {
                                // Create a new entry for this timestamp
                                const newEntry = {
                                    date: timestamp,
                                    day: day,
                                    Pages: pages,
                                };

                                totalPagesData.push(newEntry);
                            }
                        }
                    });

                    if (userBillingData.billingItems.length > 0) {
                        filteredData.push(userBillingData);
                    }
                });

                // sort the data by timestamp in ascending order
                totalBillingData.sort((a, b) => a.date - b.date);
                totalPagesData.sort((a, b) => a.date - b.date);

                // sort the data by items for filteredData
                filteredData.sort((a, b) => b.billingItems.length - a.billingItems.length);

                // calculate total Requests
                let totalRequests = 0;
                totalBillingData.forEach((entry) => {
                    totalRequests += entry.Requests;
                });

                // calculate total Pages
                let totalPages = 0;
                totalPagesData.forEach((entry) => {
                    totalPages += entry.Pages;
                });

                setTotalRequests(totalRequests);
                setTotalPages(totalPages);
                setBillingChart(totalBillingData);
                setPagesChart(totalPagesData);
                setBilling(filteredData);

                toast.success('Billing data fetched successfully', { id: toastId });
            } else {
                toast.error('No data available', { id: toastId });
            }
        }).catch((error) => {
            console.error('Error fetching data:', error);
            toast.error('Error fetching data', { id: toastId });
        });
    }

    useEffect(() => {
        const fetchData = async () => {
            await fetchBilling();
        }
        fetchData();
    }, [startTime, endTime, selectedUser]);

    return (
        <div className="">
            <ModalCustom open={open} setOpen={setOpen} >
                <ShowUserDetails userId={selectedUserId} closeModal={() => {
                    setOpen(false);
                    setSelectedUserId('');
                }} />
            </ModalCustom>

            {selectedUser !== '' && (
                <div className='mt-4'>
                    <div className="flex items-center">
                        <p className="text-sm font-semibold leading-6 text-gray-900 mr-1">Filter by user:</p>
                    </div>
                    <span
                        onClick={() => changeSelectedUser('')}
                        className="cursor-pointer inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                        {selectedUser ? users[selectedUser] : 'All'}
                        <button
                            type="button"
                            className="group relative -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                        >
                            <span className="sr-only">Remove</span>
                            <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75">
                                <path d="M4 4l6 6m0-6l-6 6" />
                            </svg>
                            <span className="absolute -inset-1" />
                        </button>
                    </span>
                </div>
            )}

            {/* Combined Date Controls and Billing Chart */}
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Date Controls Card - 1/3 */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full flex flex-col">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Range</h3>

                        {/* Current Date Range Display */}
                        <div className="space-y-3 mb-6">
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Start Date</span>
                                <span className="text-sm text-gray-900 mt-1">{formatDateLong(startTime)}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">End Date</span>
                                <span className="text-sm text-gray-900 mt-1">{formatDateLong(endTime)}</span>
                            </div>
                        </div>

                        {/* Date Range Picker */}
                        <div className="space-y-4 flex-1 flex flex-col justify-end">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Select New Range
                                </label>
                                <DateRangePickerWidget updateDates={updateDates} />
                            </div>

                            <button
                                onClick={() => updateDatesAndFetch()}
                                className="w-full inline-flex justify-center items-center gap-x-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                                Update Date Range
                            </button>
                        </div>
                    </div>
                </div>

                {/* Billing Chart - 2/3 */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full flex flex-col">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Billing Overview</h3>
                            <div className="flex items-center space-x-6">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-indigo-600">{totalRequests}</div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wide">Requests</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-green-600">{totalPages}</div>
                                    <div className="text-xs text-gray-500 uppercase tracking-wide">Pages</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 min-h-0">
                            <BillingChart billingChart={billingChart} pagesChart={pagesChart} />
                        </div>
                    </div>
                </div>
            </div>

            {extractaAiBilling.length > 0 &&
                <div className="mt-4 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-2">Extracta LABS Marketplaces</h3>
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                User
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Pages
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Requests
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                API
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Web
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Errors
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {extractaAiBilling.map((userBilling, index) => (
                                            <tr
                                                key={index}
                                            >
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 inline-flex items-center justify-center">
                                                    <p
                                                        onClick={() => changeSelectedUser(userBilling.userId)}
                                                        className="hover:text-indigo-600 cursor-pointer"
                                                    >
                                                        {users[userBilling.userId] ? users[userBilling.userId] : userBilling.userId}
                                                    </p>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.reduce((total, item) => total + (item.pagesNo || 1), 0)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === "api" || billingItem.origin === "syncAPI" || billingItem.origin === "edenAi").length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === "web").length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === undefined).length}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            }

            {teamBilling.length > 0 && (
                <div className="mt-4 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-2">Extracta LABS Team</h3>
                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                User
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Pages
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Requests
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                API
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Web
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Errors
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {teamBilling.map((userBilling, index) => (
                                            <tr key={index}>
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                    {users[userBilling.userId] ? users[userBilling.userId] : userBilling.userId}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.reduce((total, item) => total + (item.pagesNo || 1), 0)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === "api" || billingItem.origin === "syncAPI" || billingItem.origin === "edenAi").length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === "web").length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === undefined).length}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {otherBilling.length > 0 &&
                <div className="mt-4 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">Others</h3>

                            {/* Modern Pagination Bar */}
                            <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 mb-4">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                    {/* Left side - Search */}
                                    <div className="flex items-center space-x-3">
                                        <label htmlFor="searchEmail" className="text-sm font-medium text-gray-700">
                                            Search:
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                </svg>
                                            </div>
                                            <input
                                                id="searchEmail"
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="Enter email..."
                                                className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                            />
                                        </div>
                                    </div>

                                    {/* Center - Page Info */}
                                    <div className="flex items-center justify-center lg:flex-1">
                                        <div className="bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700 font-medium">
                                            Page {currentPage} of {paginationData.totalOtherBillingPages} • {filteredOtherBilling.length} of {otherBilling.length} items
                                        </div>
                                    </div>

                                    {/* Right side - Controls */}
                                    <div className="flex items-center space-x-4">
                                        {/* Items per page */}
                                        <div className="flex items-center space-x-2">
                                            <label htmlFor="itemsPerPage" className="text-sm font-medium text-gray-700">
                                                Show:
                                            </label>
                                            <select
                                                id="itemsPerPage"
                                                value={itemsPerPage}
                                                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                                className="block w-20 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                            </select>
                                        </div>

                                        {/* Navigation buttons */}
                                        <div className="flex items-center space-x-1">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                            >
                                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                                Previous
                                            </button>
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalOtherBillingPages))}
                                                disabled={currentPage === paginationData.totalOtherBillingPages}
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                            >
                                                Next
                                                <svg className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                User
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Pages
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Requests
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                API
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Web
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Errors
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {paginationData.currentOtherBilling.map((userBilling, index) => (
                                            <tr
                                                key={index}
                                                onMouseEnter={() => setHoveredRowIndex(index)}
                                                onMouseLeave={() => setHoveredRowIndex(null)}
                                                className="hover:bg-gray-100"
                                            >
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 inline-flex items-center justify-center">
                                                    <p
                                                        onClick={() => changeSelectedUser(userBilling.userId)}
                                                        className="hover:text-indigo-600 cursor-pointer"
                                                    >
                                                        {users[userBilling.userId] ? users[userBilling.userId] : userBilling.userId}
                                                    </p>
                                                    {hoveredRowIndex === index && (
                                                        <button
                                                            onClick={() => handleButtonClick(userBilling.userId)}
                                                            className="ml-2 px-2 bg-indigo-500 hover:bg-indigo-700 text-white font-bold rounded"
                                                        >
                                                            Show
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.reduce((total, item) => total + (item.pagesNo || 1), 0)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === "api" || billingItem.origin === "syncAPI" || billingItem.origin === "edenAi").length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === "web").length}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {userBilling.billingItems.filter((billingItem) => billingItem.origin === undefined).length}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            }

        </div>
    )
}